// src/hooks/useHiddenUids.js
// Faz 1F Görev 4 — Birleşik "gizli kullanıcılar" hook'u.
//
// blockedSet ∪ mutedSet = hiddenSet
//
// Tüm okuma-tarafı filtreler (feed, yorumlar, bildirimler) artık bu hook'u
// kullanır. Eskiden sadece `useBlockedUids` vardı — şimdi mute de dahil.
//
// filterByHidden: useBlockedUids.filterByBlocked ile aynı saf helper,
// isim değişikliği semantik netlik sağlıyor. Eski filterByBlocked hâlâ
// export ediliyor (backward compat) ama tüketiciler geçiş yapmalı.

import { useEffect, useMemo, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../firebase";
import { getBlockedUids } from "../services/userBlocks";
// P2-6 — mute artık tip taşır: "all" | "posts" | "stories".
import { getMutedMap } from "../services/userMutes";

/**
 * Blocked + muted uid'leri birleşik Set olarak verir.
 *
 * P2-6 — muteType ayrımı:
 *   - mutedSet / hiddenSet: "all" + "posts" mute'ları (feed/içerik filtreleri
 *     için — eski davranışla uyumlu; "stories"-only mute postları GİZLEMEZ).
 *   - storyMutedSet: "all" + "stories" mute'ları (story ring filtresi).
 *
 * (Dönüş şekli aşağıdaki gövdeden TÜRETİLİR; JSDoc'ta elle sayılmaz —
 *  `useSpotlight`ta aynı elle-sayımın alan kaçırdığı ölçüldü.)
 */
export function useHiddenUids(opts: { viewerId?: string } = {}) {
  const initialViewer = opts.viewerId || auth.currentUser?.uid || null;
  const [viewerId, setViewerId] = useState(initialViewer);
  const [blockedUids, setBlockedUids] = useState<string[]>([]);
  // Değer `"all" | "posts" | "stories"` bekleniyor ama Firestore'dan geliyor
  // ve doğrulanmıyor → `unknown`; iki `useMemo` de zaten `=== "all"` gibi
  // karşılaştırmalarla süzüyor.
  const [mutedMap, setMutedMap] = useState<Record<string, unknown>>({});
  const [ready, setReady] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (opts.viewerId) return undefined;
    const unsub = onAuthStateChanged(auth, (u) => {
      setViewerId(u?.uid || null);
    });
    return () => unsub();
  }, [opts.viewerId]);

  useEffect(() => {
    let cancelled = false;
    if (!viewerId) {
      setBlockedUids([]);
      setMutedMap({});
      setReady(true);
      return () => {};
    }
    setReady(false);
    Promise.all([
      getBlockedUids(viewerId, { force: tick > 0 }),
      getMutedMap(viewerId, { force: tick > 0 }),
    ])
      .then(([blocked, muted]) => {
        if (!cancelled) {
          setBlockedUids(Array.isArray(blocked) ? (blocked as string[]) : []);
          setMutedMap(muted && typeof muted === "object" ? (muted as Record<string, unknown>) : {});
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBlockedUids([]);
          setMutedMap({});
          setReady(true);
        }
      });
    return () => { cancelled = true; };
  }, [viewerId, tick]);

  const blockedSet = useMemo(() => new Set(blockedUids.map(String)), [blockedUids]);
  // P2-6 — feed/içerik filtreleri: "all" + "posts" mute'ları.
  const mutedSet = useMemo(() => {
    const s = new Set<string>();
    for (const [uid, type] of Object.entries(mutedMap)) {
      if (type === "all" || type === "posts") s.add(String(uid));
    }
    return s;
  }, [mutedMap]);
  // P2-6 — story ring filtresi: "all" + "stories" mute'ları.
  const storyMutedSet = useMemo(() => {
    const s = new Set<string>();
    for (const [uid, type] of Object.entries(mutedMap)) {
      if (type === "all" || type === "stories") s.add(String(uid));
    }
    return s;
  }, [mutedMap]);

  const hiddenSet = useMemo(() => {
    const s = new Set(blockedSet);
    for (const uid of Array.from(mutedSet)) s.add(uid);
    return s;
  }, [blockedSet, mutedSet]);

  const refresh = () => setTick((t) => t + 1);

  return { hiddenSet, blockedSet, mutedSet, storyMutedSet, ready, refresh };
}

/**
 * Verilen item array'ini gizli uid setine göre filtreler.
 * Null/empty set durumunda dizinin kendisini döndürür (stabil referans).
 *
 *
 * ⚠️ BU JSDoc ZATEN JENERİKTİ (`@template T`) — yani DOĞRU sözleşme yazılıydı,
 * sadece DERLENMİYORDU. TS'e çevirmek yeni bir karar değil, var olan niyeti
 * derleyiciye göstermek. Bu oturumda aynı sınıfın beşinci örneği
 * (hasTurnServer · venueClaim · useSpotlight · clipsFeed ×4 · bu).
 *
 * ── NEDEN JENERİK ŞART: ÖLÇÜLMÜŞ BİR OLAY ────────────────────────────────
 * `any[]` dönerken bu yardımcı ÇAĞIRANIN ELEMAN TİPİNİ SİLİYORDU. Ölçüm
 * (#794 turu): jenerik yapılınca KÜMEDEKİ `CommentSheetV3` 0 → 10 hataya
 * çıktı — yani o dosyanın "temiz" sayılması, KENDİ `any`si yüzünden değil,
 * KOMŞUSUNUN `any`si yüzündendi. Koruma dolaylı olarak satın alınmıştı.
 *   🧭 `any` ile satın alınan sıfır, sıfır değildir — ve borç, `any`nin
 *      yazıldığı dosyada durmak zorunda değil.
 *
 * `hiddenSet` opsiyonel: `useHiddenUids` hazır olmadan `undefined` gelebiliyor
 * ve gövde zaten `!hiddenSet` dalıyla bunu karşılıyor.
 *
 * ── İKİ TASARIM KARARI, İKİSİ DE GEVŞEK KAPININ ÖLÇÜMÜNDEN ÇIKTI ─────────
 * (1) `T = Record<string, unknown>` VARSAYILANI. Sebep: KİLİTLİ
 *     `FeedContainerV3:1401` bu yardımcıyı `any` bir diziyle çağırıyor.
 *     Derleyiciye sorduk (`ts.TypeChecker`): orada `base` = `any`. Argüman
 *     `any` olunca `T[] | null | undefined` BİRLEŞİMİNE karşı çıkarım aday
 *     üretemiyor ve `T` sessizce `unknown`a düşüyor → geri çağrıda
 *     `c?.authorId` derlenmiyor. Varsayılan, çıkarım başarısız olduğunda
 *     `unknown` yerine devreye girer. Çıkarımın ÇALIŞTIĞI 9 çağrı yerinde
 *     hiçbir etkisi yok — onlar kendi eleman tipini almaya devam eder.
 *     ⚠️ Kilitli dosyaya DOKUNULMADI; sorun çağıranda değil, imzada çözüldü.
 * (2) Geri çağrının DÖNÜŞÜ `unknown`. Gövde o değerle SADECE iki şey yapıyor:
 *     doğruluk testi (`if (!aid)`) ve `String(aid)`. İkisi de `unknown`da
 *     çalışır. `string | number | null | undefined` yazmak, gövdenin
 *     yapmadığı bir vaat vermek olurdu — ve (1)'den sonra kilitli dosyada
 *     tam olarak o vaat düşüyordu.
 */
export function filterByHidden<T = Record<string, unknown>>(
  items: T[] | null | undefined,
  getAuthorId: (item: T) => unknown,
  hiddenSet?: Set<string> | null,
): T[] {
  if (!Array.isArray(items) || items.length === 0) return items || [];
  if (!hiddenSet || hiddenSet.size === 0) return items;
  return items.filter((it) => {
    const aid = getAuthorId(it);
    if (!aid) return true;
    return !hiddenSet.has(String(aid));
  });
}

export default useHiddenUids;
